import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Clock, Zap, DollarSign, Trophy, Activity, Eye, EyeOff, CheckCircle, XCircle, Info, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import axios from 'axios';
import CryptoIcon from './CryptoIcon';

const API_URL = '';

// Comprehensive crypto pairs - matching deposit section cryptocurrencies + GOLD
const CRYPTO_PAIRS = [
  { symbol: 'BTC/USDT', name: 'Bitcoin', icon: 'BTC', color: '#F7931A' },
  { symbol: 'ETH/USDT', name: 'Ethereum', icon: 'ETH', color: '#627EEA' },
  { symbol: 'GOLD/USDT', name: 'Gold', icon: 'GOLD', color: '#FFD700' },
  { symbol: 'USDC/USDT', name: 'USD Coin', icon: 'USDC', color: '#2775CA' },
  { symbol: 'BNB/USDT', name: 'Binance Coin', icon: 'BNB', color: '#F3BA2F' },
  { symbol: 'SOL/USDT', name: 'Solana', icon: 'SOL', color: '#9945FF' },
  { symbol: 'XRP/USDT', name: 'Ripple', icon: 'XRP', color: '#00AAE4' },
  { symbol: 'ADA/USDT', name: 'Cardano', icon: 'ADA', color: '#0033AD' },
  { symbol: 'DOGE/USDT', name: 'Dogecoin', icon: 'DOGE', color: '#C3A634' },
  { symbol: 'AVAX/USDT', name: 'Avalanche', icon: 'AVAX', color: '#E84142' },
  { symbol: 'DOT/USDT', name: 'Polkadot', icon: 'DOT', color: '#E6007A' },
  { symbol: 'MATIC/USDT', name: 'Polygon', icon: 'MATIC', color: '#8247E5' },
  { symbol: 'LTC/USDT', name: 'Litecoin', icon: 'LTC', color: '#345D9D' },
  { symbol: 'LINK/USDT', name: 'Chainlink', icon: 'LINK', color: '#2A5ADA' },
  { symbol: 'UNI/USDT', name: 'Uniswap', icon: 'UNI', color: '#FF007A' },
  { symbol: 'ATOM/USDT', name: 'Cosmos', icon: 'ATOM', color: '#2E3148' },
  { symbol: 'XLM/USDT', name: 'Stellar', icon: 'XLM', color: '#14B6E7' },
  { symbol: 'ALGO/USDT', name: 'Algorand', icon: 'ALGO', color: '#000000' },
  { symbol: 'FTM/USDT', name: 'Fantom', icon: 'FTM', color: '#13B5EC' },
  { symbol: 'NEAR/USDT', name: 'NEAR Protocol', icon: 'NEAR', color: '#000000' },
  { symbol: 'AAVE/USDT', name: 'Aave', icon: 'AAVE', color: '#B6509E' },
];

const EnhancedBinaryTrading = ({ onBalanceUpdate }) => {
  const { toast } = useToast();
  const { balanceVisible, formatBalance } = useBalanceVisibility();
  const audioRef = useRef(null);
  
  // State management
  const [selectedPair, setSelectedPair] = useState(CRYPTO_PAIRS[0]);
  const [durations, setDurations] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [tradeAmount, setTradeAmount] = useState('');
  const [direction, setDirection] = useState('up');
  const [balance, setBalance] = useState(null);
  const [activeTrades, setActiveTrades] = useState([]);
  const [completedTrades, setCompletedTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('trade');
  const [selectedTradeDetail, setSelectedTradeDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [shownNotifications, setShownNotifications] = useState(new Set());
  const [syncVersion, setSyncVersion] = useState(0);  // Track balance sync version
  const [isPlacingTrade, setIsPlacingTrade] = useState(false);  // Prevent double clicks
  
  // Fetch durations
  const fetchDurations = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/api/binary/durations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setDurations(res.data.durations);
        if (res.data.durations.length > 0) {
          setSelectedDuration(res.data.durations[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching durations:', error);
    }
  };

  // Fast real-time sync - only fetches changes, not full data
  const realtimeSync = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/api/binary/realtime/sync?since_version=${syncVersion}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        // Update balance if changed
        if (res.data.balance.has_changes) {
          setBalance(res.data.balance.usdt_balance);
          setSyncVersion(res.data.balance.sync_version);
          
          // Trigger parent balance update
          if (onBalanceUpdate) {
            onBalanceUpdate();
          }
        }
        
        // Update active trades with real-time progress
        if (res.data.trades.trades.length > 0) {
          setActiveTrades(prevTrades => {
            // Merge real-time data with existing trades
            const updatedTrades = prevTrades.map(trade => {
              const realtimeTrade = res.data.trades.trades.find(t => t.trade_id === trade.trade_id);
              if (realtimeTrade) {
                return {
                  ...trade,
                  remaining_seconds: realtimeTrade.remaining_seconds,
                  progress_percent: realtimeTrade.progress_percent,
                  is_expiring_soon: realtimeTrade.is_expiring_soon,
                  is_expired: realtimeTrade.is_expired
                };
              }
              return trade;
            });
            return updatedTrades;
          });
        }
        
        // Handle recently completed trades
        if (res.data.recently_completed.length > 0) {
          // Trigger full data refresh
          fetchData();
          checkNotifications();
        }
      }
    } catch (error) {
      // Fallback to full fetch if realtime sync fails
      console.error('Realtime sync error, falling back to full fetch:', error);
    }
  };

  // Fetch all data (full refresh)
  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const prevActiveCount = activeTrades.length;
    const prevCompletedCount = completedTrades.length;

    try {
      // Fetch balance
      const balanceRes = await axios.get(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (balanceRes.data.success) {
        setBalance(balanceRes.data.balance);
      }

      // Fetch active trades
      const activeRes = await axios.get(`${API_URL}/api/binary/active-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activeRes.data.success) {
        setActiveTrades(activeRes.data.trades);
      }

      // Fetch completed trades
      const completedRes = await axios.get(`${API_URL}/api/binary/completed-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (completedRes.data.success) {
        const newCompletedCount = completedRes.data.trades.length;
        setCompletedTrades(completedRes.data.trades);
        
        // If new trades completed, update Trading Hub balance
        if (newCompletedCount > prevCompletedCount && onBalanceUpdate) {
          onBalanceUpdate();
        }
      }

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/binary/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }

      // Check for notifications
      checkNotifications();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Check for new trade completions (only show once per notification)
  const checkNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/api/binary/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success && res.data.notifications.length > 0) {
        const notificationsToMarkRead = [];
        
        // Process each notification
        res.data.notifications.forEach(notif => {
          const notifId = notif.notification_id || notif.trade_id;
          
          // Only show if we haven't shown this notification in this session
          if (!shownNotifications.has(notifId)) {
            const isWin = notif.outcome === 'win';
            
            toast({
              title: isWin ? '🎉 Trade Won!' : '📉 Trade Lost',
              description: isWin 
                ? `Congratulations! You won $${Math.abs(notif.profit || 0).toFixed(2)} on ${notif.pair}`
                : `Trade closed. Loss: $${Math.abs(notif.profit || 0).toFixed(2)} on ${notif.pair}`,
              variant: isWin ? 'default' : 'destructive',
              duration: 5000,  // Auto-dismiss after 5 seconds
            });
            
            // Mark this notification as shown in local state
            setShownNotifications(prev => new Set([...prev, notifId]));
            
            // Add to list of notifications to mark as read on server
            notificationsToMarkRead.push(notifId);
          }
        });
        
        // Mark notifications as read on the server so they don't appear again
        if (notificationsToMarkRead.length > 0) {
          try {
            await axios.post(
              `${API_URL}/api/binary/notifications/mark-read`,
              { notification_ids: notificationsToMarkRead },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (markError) {
            console.error('Error marking notifications as read:', markError);
          }
        }
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  useEffect(() => {
    fetchDurations();
    fetchData();
    
    // Full data refresh every 10 seconds
    const fullRefreshInterval = setInterval(fetchData, 10000);
    
    // Fast realtime sync every 2 seconds for active trades
    const realtimeSyncInterval = setInterval(realtimeSync, 2000);
    
    return () => {
      clearInterval(fullRefreshInterval);
      clearInterval(realtimeSyncInterval);
    };
  }, []);

  // Get available durations based on amount
  const getAvailableDurations = () => {
    if (!tradeAmount || tradeAmount === '') return durations;
    
    const amount = parseFloat(tradeAmount);
    if (isNaN(amount)) return durations;
    
    return durations.filter(d => 
      amount >= d.min_amount && amount <= d.max_amount
    );
  };

  // Place trade with optimistic balance update
  const placeTrade = async () => {
    if (!selectedDuration || !tradeAmount || !selectedPair) {
      toast({
        title: 'Missing Information',
        description: 'Please select all trade parameters',
        variant: 'destructive',
      });
      return;
    }

    const amount = parseFloat(tradeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount',
        variant: 'destructive',
      });
      return;
    }

    if (amount < selectedDuration.min_amount || amount > selectedDuration.max_amount) {
      toast({
        title: 'Amount Out of Range',
        description: `For ${selectedDuration.label}, amount must be between $${selectedDuration.min_amount.toLocaleString()} and $${selectedDuration.max_amount.toLocaleString()}`,
        variant: 'destructive',
      });
      return;
    }

    // Prevent double-click
    if (isPlacingTrade) return;
    setIsPlacingTrade(true);
    setLoading(true);

    // Optimistic balance update - immediately show deducted balance
    const previousBalance = balance;
    setBalance(prevBalance => prevBalance - amount);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/binary/trade`, {
        pair: selectedPair.symbol,
        amount: amount,
        direction: direction,
        duration: selectedDuration.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        // Update balance from server response (authoritative)
        if (res.data.balance_update) {
          setBalance(res.data.balance_update.new_balance);
          setSyncVersion(res.data.balance_update.sync_version);
        }

        toast({
          title: '✅ Trade Placed!',
          description: `${direction.toUpperCase()} trade on ${selectedPair.symbol} for $${amount.toLocaleString()} | Potential profit: $${res.data.trade.potential_profit?.toFixed(2) || '0.00'}`,
        });

        setTradeAmount('');
        setCurrentView('active');
        
        // Add the new trade to active trades immediately
        if (res.data.trade) {
          setActiveTrades(prev => [{
            trade_id: res.data.trade.trade_id,
            pair: res.data.trade.pair,
            amount: res.data.trade.amount,
            direction: res.data.trade.direction,
            duration: res.data.trade.duration,
            duration_seconds: res.data.trade.duration_seconds,
            roi_percentage: res.data.trade.roi_percentage,
            expiry_time: res.data.trade.expiry_time,
            start_time: res.data.trade.start_time,
            remaining_seconds: res.data.trade.duration_seconds,
            status: 'active'
          }, ...prev]);
        }
        
        // Update balance in Trading Hub
        if (onBalanceUpdate) {
          onBalanceUpdate();
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setBalance(previousBalance);
      
      toast({
        title: 'Trade Failed',
        description: error.response?.data?.detail || 'Failed to place trade',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsPlacingTrade(false);
    }
  };

  // Format countdown timer
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate remaining time and check for alerts
  const getRemainingTime = (expiryTime) => {
    if (!expiryTime) return 0;
    
    const now = new Date();
    let expiry = new Date(expiryTime);
    
    // Handle timezone - if expiry time is in ISO format with Z, it's UTC
    if (typeof expiryTime === 'string' && expiryTime.includes('Z')) {
      expiry = new Date(expiryTime);
    } else if (typeof expiryTime === 'string') {
      // Parse as UTC if no timezone info
      expiry = new Date(expiryTime + 'Z');
    }
    
    const diff = Math.floor((expiry - now) / 1000);
    return Math.max(0, diff);
  };

  // View trade details
  const viewTradeDetails = async (tradeId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API_URL}/api/binary/trade/${tradeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setSelectedTradeDetail(res.data.trade);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load trade details',
        variant: 'destructive',
      });
    }
  };

  // Countdown timer component with rotating indicator
  const CountdownTimer = ({ expiryTime, tradeId, durationSeconds, initialRemaining }) => {
    // Use backend's remaining_seconds as the initial value (more accurate)
    const [remaining, setRemaining] = useState(initialRemaining || getRemainingTime(expiryTime));
    const [hasAlerted, setHasAlerted] = useState(false);

    useEffect(() => {
      // Update from initial remaining if provided
      if (initialRemaining !== undefined && initialRemaining !== remaining) {
        setRemaining(initialRemaining);
      }
    }, [initialRemaining]);

    useEffect(() => {
      const timer = setInterval(() => {
        // Decrement locally for smooth countdown
        setRemaining(prev => {
          const newVal = Math.max(0, prev - 1);
          
          // Visual/audio alert at 10 seconds
          if (newVal === 10 && !hasAlerted) {
            setHasAlerted(true);
            toast({
              title: '⚠️ Trade Expiring Soon!',
              description: `Trade expires in 10 seconds`,
              duration: 3000,
            });
            // Play alert sound if audio element exists
            if (audioRef.current) {
              audioRef.current.play().catch(err => console.log('Audio play failed:', err));
            }
          }

          if (newVal === 0) {
            clearInterval(timer);
            setTimeout(() => fetchData(), 2000); // Refresh after expiry
          }
          
          return newVal;
        });
      }, 1000);

      return () => clearInterval(timer);
    }, [tradeId, hasAlerted]);

    const isWarning = remaining <= 10 && remaining > 0;
    const isExpired = remaining === 0;
    
    // Calculate progress percentage for circular indicator (elapsed time)
    const progress = durationSeconds ? ((durationSeconds - remaining) / durationSeconds) * 100 : 0;
    const circumference = 2 * Math.PI * 40;

    return (
      <div className="flex flex-col items-center gap-4">
        {/* Clean Circular Progress Indicator */}
        <div className="relative w-28 h-28 flex items-center justify-center">
          {/* Rotating background ring - continuous spin */}
          {!isExpired && (
            <svg className="absolute w-28 h-28 animate-spin" style={{ animationDuration: '2s' }}>
              <circle
                cx="56"
                cy="56"
                r="52"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray="8 4"
                className={`${isWarning ? 'text-yellow-400/30' : 'text-green-400/30'}`}
              />
            </svg>
          )}

          {/* Main progress circle */}
          <svg className="w-28 h-28 transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="56"
              cy="56"
              r="40"
              stroke="currentColor"
              strokeWidth="6"
              fill="none"
              className="text-slate-700/50"
            />
            {/* Progress circle - fills as time passes */}
            <circle
              cx="56"
              cy="56"
              r="40"
              stroke="currentColor"
              strokeWidth="6"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={circumference - (progress / 100) * circumference}
              className={`transition-all duration-1000 ${
                isWarning ? 'text-yellow-400' : isExpired ? 'text-red-400' : 'text-green-400'
              }`}
              strokeLinecap="round"
            />
          </svg>

          {/* Center display - seconds remaining */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`text-3xl font-bold tabular-nums ${
                isWarning ? 'text-yellow-400' : isExpired ? 'text-red-400' : 'text-green-400'
              }`}>
                {remaining}
              </div>
              <div className={`text-sm font-semibold ${
                isWarning ? 'text-yellow-400/70' : isExpired ? 'text-red-400/70' : 'text-green-400/70'
              }`}>
                seconds
              </div>
            </div>
          </div>
        </div>

        {/* Status and Info */}
        <div className="text-center space-y-2">
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
            isExpired 
              ? 'bg-red-500/20 border border-red-500/50' 
              : isWarning 
                ? 'bg-yellow-500/20 border border-yellow-500/50' 
                : 'bg-green-500/20 border border-green-500/50'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isExpired ? 'bg-red-400' : isWarning ? 'bg-yellow-400 animate-pulse' : 'bg-green-400 animate-pulse'
            }`}></div>
            <span className={`text-sm font-bold ${
              isExpired ? 'text-red-400' : isWarning ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {isExpired ? 'COMPLETED' : 'PENDING'}
            </span>
            {isWarning && !isExpired && (
              <AlertTriangle className="w-4 h-4 text-yellow-400 animate-bounce" />
            )}
          </div>

          {/* Time and Duration Info */}
          <div className="text-gray-400 text-sm space-y-1">
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-mono">
                {isExpired ? 'Trade Complete' : `${formatTime(remaining)} remaining`}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Total Duration: {durationSeconds}s ({Math.floor(durationSeconds / 60)}:{(durationSeconds % 60).toString().padStart(2, '0')})
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Hidden audio element for alert sound */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=" />

      {/* Trading Stats Display - Balance removed (shown in parent component) */}
      {stats && (
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 rounded-xl border border-cyan-500/30">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xs text-gray-400">Active</div>
              <div className="text-lg font-bold text-cyan-400">{stats.active_trades}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Wins</div>
              <div className="text-lg font-bold text-green-400">{stats.total_wins}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Win Rate</div>
              <div className="text-lg font-bold text-purple-400">{stats.win_rate}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 bg-slate-900/50 p-2 rounded-xl border border-cyan-500/20">
        {['trade', 'active', 'history'].map(view => (
          <button
            key={view}
            onClick={() => setCurrentView(view)}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
              currentView === view
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {view.charAt(0).toUpperCase() + view.slice(1)}
          </button>
        ))}
      </div>

      {/* Trade View */}
      {currentView === 'trade' && (
        <div className="space-y-4">
          {/* Pair Selection */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Select Trading Pair</label>
            <div className="relative">
              <select
                value={selectedPair.symbol}
                onChange={(e) => {
                  const pair = CRYPTO_PAIRS.find(p => p.symbol === e.target.value);
                  if (pair) setSelectedPair(pair);
                }}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none appearance-none cursor-pointer"
                style={{
                  backgroundImage: `linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)`,
                  backgroundPosition: 'calc(100% - 20px) calc(1em + 2px), calc(100% - 15px) calc(1em + 2px)',
                  backgroundSize: '5px 5px, 5px 5px',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                {CRYPTO_PAIRS.map(pair => (
                  <option key={pair.symbol} value={pair.symbol}>
                    {pair.icon} - {pair.name} ({pair.symbol})
                  </option>
                ))}
              </select>
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <CryptoIcon symbol={selectedPair.icon} size="sm" />
              </div>
            </div>
            <div className="mt-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Selected:</span>
                <span className="text-sm font-semibold" style={{ color: selectedPair.color }}>
                  {selectedPair.symbol}
                </span>
              </div>
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Trade Amount (USDT)</label>
            <input
              type="number"
              value={tradeAmount}
              onChange={(e) => setTradeAmount(e.target.value)}
              placeholder="Enter amount ($500 - $500,000)"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
            />
            <div className="flex gap-2 mt-2">
              {[500, 1000, 5000, 10000, 50000, 100000].map(amt => (
                <button
                  key={amt}
                  onClick={() => setTradeAmount(amt.toString())}
                  className="px-3 py-1 bg-slate-800 border border-slate-700 rounded text-sm hover:border-cyan-500 transition-colors"
                >
                  ${amt >= 1000 ? (amt / 1000) + 'K' : amt}
                </button>
              ))}
            </div>
          </div>

          {/* Duration Selection */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Select Duration</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {getAvailableDurations().map(dur => (
                <button
                  key={dur.id}
                  onClick={() => setSelectedDuration(dur)}
                  disabled={!tradeAmount || parseFloat(tradeAmount) < dur.min_amount || parseFloat(tradeAmount) > dur.max_amount}
                  className={`p-3 rounded-lg border transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                    selectedDuration?.id === dur.id
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="text-sm font-bold text-white">{dur.label}</div>
                  <div className="text-xs text-cyan-400">ROI: {dur.roi_percentage}%</div>
                  <div className="text-xs text-gray-500">${(dur.min_amount/1000).toFixed(1)}K-${(dur.max_amount/1000).toFixed(1)}K</div>
                </button>
              ))}
            </div>
            {tradeAmount && getAvailableDurations().length === 0 && (
              <p className="text-sm text-yellow-400 mt-2">⚠️ No durations available for this amount</p>
            )}
          </div>

          {/* Direction Selection */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setDirection('up')}
              className={`py-4 rounded-lg font-bold text-lg transition-all ${
                direction === 'up'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              <TrendingUp className="w-6 h-6 mx-auto mb-1" />
              UP
            </button>
            <button
              onClick={() => setDirection('down')}
              className={`py-4 rounded-lg font-bold text-lg transition-all ${
                direction === 'down'
                  ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/50'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              <TrendingDown className="w-6 h-6 mx-auto mb-1" />
              DOWN
            </button>
          </div>

          {/* Trade Summary & Button */}
          {selectedDuration && tradeAmount && (
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Investment:</span>
                  <span className="text-white font-bold">${parseFloat(tradeAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Potential Profit:</span>
                  <span className="text-green-400 font-bold">
                    ${(parseFloat(tradeAmount) * selectedDuration.roi_percentage / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Return:</span>
                  <span className="text-cyan-400 font-bold">
                    ${(parseFloat(tradeAmount) + (parseFloat(tradeAmount) * selectedDuration.roi_percentage / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={placeTrade}
            disabled={loading || !selectedDuration || !tradeAmount}
            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Placing Trade...' : 'Place Trade'}
          </button>
        </div>
      )}

      {/* Active Trades View */}
      {currentView === 'active' && (
        <div className="space-y-4">
          {activeTrades.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Activity className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No active trades</p>
            </div>
          ) : (
            activeTrades.map(trade => (
              <div key={trade.trade_id} className="bg-slate-800/50 p-5 rounded-lg border border-slate-700">
                {/* Trade Info Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-white text-lg">{trade.pair}</span>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      trade.direction === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {trade.direction.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Countdown Timer - Single Centered Display */}
                <div className="flex justify-center mb-4">
                  <CountdownTimer 
                    expiryTime={trade.expiry_time} 
                    tradeId={trade.trade_id}
                    durationSeconds={trade.duration_seconds}
                    initialRemaining={trade.remaining_seconds}
                  />
                </div>

                {/* Trade Details */}
                <div className="grid grid-cols-3 gap-4 text-sm pt-4 border-t border-slate-700">
                  <div>
                    <div className="text-gray-400">Amount</div>
                    <div className="font-bold text-white">${trade.amount}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">ROI</div>
                    <div className="font-bold text-cyan-400">{trade.roi_percentage}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Potential</div>
                    <div className="font-bold text-green-400">
                      +${(trade.amount * trade.roi_percentage / 100).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* History View */}
      {currentView === 'history' && (
        <div className="space-y-3">
          {completedTrades.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No trade history</p>
            </div>
          ) : (
            completedTrades.map(trade => (
              <div key={trade.trade_id} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                {/* Trade Summary with Win/Loss Highlight */}
                <div className="mb-3">
                  {trade.outcome === 'win' ? (
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                      <span className="text-2xl font-bold text-green-400">
                        Won +${Math.abs(trade.profit).toFixed(2)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-6 h-6 text-red-400" />
                      <span className="text-2xl font-bold text-red-400">
                        Lost -${Math.abs(trade.profit).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white">{trade.pair}</div>
                    <div className="text-sm text-gray-400">
                      {trade.direction.toUpperCase()} • {trade.duration_label || trade.duration}
                    </div>
                  </div>
                  <button
                    onClick={() => viewTradeDetails(trade.trade_id)}
                    className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                  >
                    <Info className="w-4 h-4" />
                    Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Trade Detail Modal */}
      {showDetailModal && selectedTradeDetail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-cyan-500/30 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Trade Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {/* Outcome Banner */}
              {selectedTradeDetail.outcome && (
                <div className={`p-4 rounded-lg mb-4 ${
                  selectedTradeDetail.outcome === 'win'
                    ? 'bg-green-500/20 border border-green-500/50'
                    : 'bg-red-500/20 border border-red-500/50'
                }`}>
                  <div className="flex items-center gap-3">
                    {selectedTradeDetail.outcome === 'win' ? (
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-400" />
                    )}
                    <div>
                      <div className={`text-2xl font-bold ${
                        selectedTradeDetail.outcome === 'win' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {selectedTradeDetail.outcome === 'win' ? 'Trade Won' : 'Trade Lost'}
                      </div>
                      <div className={`text-lg font-bold ${
                        selectedTradeDetail.outcome === 'win' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {selectedTradeDetail.outcome === 'win' ? '+' : '-'}${Math.abs(selectedTradeDetail.profit).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Trade Information */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Trade ID</span>
                  <span className="font-mono text-white">{selectedTradeDetail.trade_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Pair</span>
                  <span className="font-bold text-white">{selectedTradeDetail.pair}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Direction</span>
                  <span className={`font-bold ${
                    selectedTradeDetail.direction === 'up' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {selectedTradeDetail.direction.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Investment Amount</span>
                  <span className="font-bold text-white">${selectedTradeDetail.amount}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Duration</span>
                  <span className="text-white">{selectedTradeDetail.duration_label}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">ROI Percentage</span>
                  <span className="text-cyan-400 font-bold">{selectedTradeDetail.roi_percentage}%</span>
                </div>
                {selectedTradeDetail.outcome && (
                  <>
                    <div className="flex justify-between py-2 border-b border-slate-700">
                      <span className="text-gray-400">Profit/Loss</span>
                      <span className={`font-bold ${
                        selectedTradeDetail.profit >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {selectedTradeDetail.profit >= 0 ? '+' : '-'}${Math.abs(selectedTradeDetail.profit).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-700">
                      <span className="text-gray-400">Total Credited</span>
                      <span className="font-bold text-cyan-400">${selectedTradeDetail.credit_amount?.toFixed(2) || '0.00'}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Start Time</span>
                  <span className="text-white">{new Date(selectedTradeDetail.start_time).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Expiry Time</span>
                  <span className="text-white">{new Date(selectedTradeDetail.expiry_time).toLocaleString()}</span>
                </div>
                {selectedTradeDetail.completed_at && (
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-gray-400">Completed At</span>
                    <span className="text-white">{new Date(selectedTradeDetail.completed_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full mt-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedBinaryTrading;
